/**
 * The footer for the public pages.
 *
 * `full` — five columns (telc and the state exam each with their reference
 * pages, the use-case pages with the comparison, what a candidate deals with
 * before the exam, the legal pages) under a name/tagline row. Rendered on the prerendered marketing
 * pages, where it doubles as the site map a crawler follows — which is why
 * `/telc/zasady` and `/certyfikat/zasady` are listed here at all: nothing in
 * the header links to them.
 *
 * `compact` — a single wrapped row of the same links, for the screens behind
 * sign-in where the footer is just a way back out.
 *
 * Both end with our Instagram, YouTube and TikTok.
 */
import { GraduationCap } from "lucide-react";
import { Link } from "@/components/link";
import { LOCALES, useI18n } from "@/i18n";
import { localize, useRoute } from "@/lib/router";
import type { MessageKey } from "@/i18n";
import {
  BEFORE_LINKS,
  COMPACT_LINKS,
  COMPARE_LINK,
  LEGAL_LINKS,
  SCHOOL_LINKS,
  SOCIAL_LINKS,
  STATE_COLUMN_LINKS,
  TELC_COLUMN_LINKS,
  USE_CASE_LINKS,
  type NavLink,
} from "./links";

function SocialLinks({ className }: { className: string }) {
  return (
    <>
      {SOCIAL_LINKS.map((l) => (
        <a key={l.href} href={l.href} target="_blank" rel="noopener" className={className}>
          {l.label}
        </a>
      ))}
    </>
  );
}

const COLUMNS: { heading: MessageKey; links: NavLink[] }[] = [
  { heading: "footer.telc", links: TELC_COLUMN_LINKS },
  { heading: "footer.state", links: STATE_COLUMN_LINKS },
  // The decision page sits with the reasons: "which exam" is what they lead to.
  { heading: "footer.whoFor", links: [...USE_CASE_LINKS, COMPARE_LINK] },
  { heading: "footer.before", links: BEFORE_LINKS },
  { heading: "footer.legal", links: [...LEGAL_LINKS, ...SCHOOL_LINKS] },
];

export function MarketingFooter({
  variant = "full",
  wide = false,
}: {
  variant?: "full" | "compact";
  wide?: boolean;
}) {
  const column = wide ? "max-w-5xl" : "max-w-3xl";
  const { t, locale, setLocale } = useI18n();
  const route = useRoute();

  if (variant === "compact") {
    return (
      <footer className="mx-auto max-w-3xl px-4 pb-10 sm:px-5">
        <nav className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-6 text-xs text-muted-foreground">
          {[...COMPACT_LINKS, ...LEGAL_LINKS].map((l) => (
            <Link key={l.to} to={l.to} className="hover:text-foreground">
              {t(l.label)}
            </Link>
          ))}
          <SocialLinks className="hover:text-foreground" />
        </nav>
      </footer>
    );
  }

  return (
    <footer className="border-t">
      <div className={`mx-auto ${column} px-4 py-10 sm:px-5`}>
        <div className="mb-10 flex flex-col gap-2">
          <div className="flex items-center gap-2 font-extrabold tracking-tight">
            <GraduationCap className="size-5 shrink-0" />
            {t("app.name")}
          </div>
          <p className="max-w-[46ch] text-xs leading-relaxed text-muted-foreground">
            {t("app.tagline")}
          </p>
        </div>

        <div className={`grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 ${wide ? "lg:grid-cols-5" : ""}`}>
          {COLUMNS.map((col) => (
            <nav key={col.heading} aria-label={t(col.heading)} className="flex flex-col gap-2.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(col.heading)}
              </p>
              {col.links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t(l.label)}
                </Link>
              ))}
            </nav>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t pt-6">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {t("app.name")}
          </p>
          {/* The other languages as plain links to this same page — what a crawler
              follows, and what a reader with a new-tab click gets. */}
          <nav aria-label={t("nav.language")} className="flex gap-4 text-xs">
            {LOCALES.map((l) => (
              <a
                key={l.id}
                href={localize(route, l.id)}
                lang={l.id}
                hrefLang={l.id}
                aria-current={l.id === locale ? "true" : undefined}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                  e.preventDefault();
                  setLocale(l.id);
                }}
                className={
                  l.id === locale
                    ? "font-semibold text-foreground"
                    : "text-muted-foreground transition-colors hover:text-foreground"
                }
              >
                {l.name}
              </a>
            ))}
          </nav>
          <nav className="flex gap-4 text-xs">
            <SocialLinks className="text-muted-foreground transition-colors hover:text-foreground" />
          </nav>
        </div>
      </div>
    </footer>
  );
}
